import { Outlet, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import type { CivilAdviseDocumentRow, CivilAdviseWizardDraft } from '../types/civilAdviseCaseRequest';
import { CIVIL_ADVISE_CATEGORY_ID, FILE_CATEGORY_CIVIL_ADVISE } from '../pages/wizard/civilAdvise/civilAdviseConstants';
import * as civilAdviseService from '../services/civilAdviseCaseRequests';
import { isDocumentTypeUuid } from '../utils/documentTypeId';

export const DEFAULT_CIVIL_ADVISE_DRAFT: CivilAdviseWizardDraft = {
  fileCategory: FILE_CATEGORY_CIVIL_ADVISE,
  fileCategoryId: '',
  subFileCategoryId: '',
  subFileCategoryName: '',
  applicableAreas: [],
  applicableAreaIds: [],
  instituteType: 'government',
  corporateInstituteId: '',
  corporateInstituteName: '',
  corporateInstituteOther: '',
  corporateInstituteContactName: '',
  corporateInstitutePrefix: '',

  description: '',
  provinceId: '',
  provinceName: '',
  districtId: '',
  districtName: '',
  yourReferenceNumber: '',
  otherExternalReferences: '',
  hasPreviousAdvice: 'no',
  previousAdviceFiles: [],
  hasConnectedCaseRelevant: 'no',
  connectedCaseDetails: '',
  caseNumber: '',
  hasConnectedMatters: 'no',
  connectedCaseNumber: '',
  connectedAgdFileRefAvailable: false,
  connectedAgdFileRef: '',

  urgencyLevelId: '',
  urgencyLevelName: '',
  deadline: '',
  urgencyJustification: '',

  deliveryMethodIds: [],
  deliveryMethods: [],
  deliveryOther: '',

  documents: [],
  confirmCorrect: false,

  draftRequestId: '',
  referenceNo: '',
};

export type CivilAdviseWizardContextValue = {
  draft: CivilAdviseWizardDraft;
  setDraft: React.Dispatch<React.SetStateAction<CivilAdviseWizardDraft>>;
  reset: () => void;
};

type WizardSeedState = { seedDraft?: Partial<CivilAdviseWizardDraft> };

function defaultConnectedMatters(
  value: CivilAdviseWizardDraft['hasConnectedMatters'] | undefined,
): CivilAdviseWizardDraft['hasConnectedMatters'] {
  return value === 'yes' || value === 'no' ? value : 'no';
}

export default function CivilAdviseRequestLayout() {
  const params = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const resumeId = (params.caseRequestId ?? searchParams.get('id') ?? '').trim();
  const [draft, setDraft] = useState<CivilAdviseWizardDraft>({ ...DEFAULT_CIVIL_ADVISE_DRAFT });
  const [loadingDraft, setLoadingDraft] = useState(Boolean(resumeId));

  useEffect(() => {
    const seed = (location.state as WizardSeedState | null)?.seedDraft;
    if (!seed) return;
    setDraft((d) => ({
      ...DEFAULT_CIVIL_ADVISE_DRAFT,
      ...d,
      ...seed,
      hasConnectedMatters: defaultConnectedMatters(seed.hasConnectedMatters ?? d.hasConnectedMatters),
    }));
    window.history.replaceState({}, document.title);
  }, [location.state]);

  // Pre-select file category so sub file category options load from the API on step 1.
  useEffect(() => {
    if (resumeId) return;
    setDraft((d) => {
      if (d.fileCategoryId) return d;
      return {
        ...d,
        fileCategoryId: CIVIL_ADVISE_CATEGORY_ID,
        fileCategory: FILE_CATEGORY_CIVIL_ADVISE,
      };
    });
  }, [resumeId]);

  useEffect(() => {
    if (!resumeId) {
      setLoadingDraft(false);
      return;
    }
    let mounted = true;
    void civilAdviseService.getCivilAdviseRequestDetails(resumeId).then((r) => {
      if (!mounted) return;
      if (r.success && r.data) {
        setDraft({
          ...DEFAULT_CIVIL_ADVISE_DRAFT,
          ...r.data,
          hasConnectedMatters: defaultConnectedMatters(r.data.hasConnectedMatters),
          documents: (r.data.documents ?? []).map((doc) => ({
            ...doc,
            files: Array.isArray(doc.files) && doc.files.length > 0
              ? doc.files
              : (doc as any).fileName
                ? [{ fileName: (doc as any).fileName, originalFileName: (doc as any).originalFileName ?? '', uploadTime: (doc as any).uploadTime ?? '' }]
                : [],
          })),
        });
      }
      setLoadingDraft(false);
    });
    return () => {
      mounted = false;
    };
  }, [resumeId]);

  const v = useMemo<CivilAdviseWizardContextValue>(
    () => ({
      draft,
      setDraft,
      reset: () => setDraft({ ...DEFAULT_CIVIL_ADVISE_DRAFT }),
    }),
    [draft],
  );

  if (loadingDraft) {
    return <div className="pg-sub">Loading case request…</div>;
  }

  return <Outlet context={v} />;
}

function normalizeDocLabel(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

function findDocumentForChecklistItem(
  prev: CivilAdviseDocumentRow[],
  item: { id: string; documentTypeId: string; name: string; documentTypeCode?: string },
): CivilAdviseDocumentRow | undefined {
  return prev.find((d) => {
    if (d.checklistItemId === item.id) return true;
    if (item.documentTypeId && d.documentTypeId === item.documentTypeId) return true;
    if (item.documentTypeCode) {
      if (d.documentTypeId === item.documentTypeCode || d.checklistItemId === item.documentTypeCode) return true;
    }
    if (!isDocumentTypeUuid(d.documentTypeId) && normalizeDocLabel(d.documentName) === normalizeDocLabel(item.name)) {
      return true;
    }
    return false;
  });
}

export function syncCivilAdviseDocumentsFromChecklist(
  prev: CivilAdviseDocumentRow[],
  checklist: { id: string; documentTypeId: string; name: string; mandatory: boolean; documentTypeCode?: string }[],
): CivilAdviseDocumentRow[] {
  return checklist.map((item) => {
    const existing = findDocumentForChecklistItem(prev, item);
    if (existing) {
      return {
        ...existing,
        checklistItemId: item.id,
        documentTypeId: item.documentTypeId,
        documentName: item.name,
        mandatory: item.mandatory,
      };
    }
    return {
      id: crypto.randomUUID(),
      checklistItemId: item.id,
      documentTypeId: item.documentTypeId,
      documentName: item.name,
      mandatory: item.mandatory,
      files: [],
      skipReason: '',
    };
  });
}
